import React from 'react';

/**
 * Tailwind CSS Test Component
 * Demonstrates the Nordic design system colors and components
 */
export const TailwindTest: React.FC = () => {
  return (
    <div className="min-h-screen bg-nordic-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-nordic-gray-900 mb-2">
            Tailwind CSS + Nordic Design System
          </h1>
          <p className="text-lg text-nordic-gray-600">
            Testing the design system components and color palette
          </p>
        </div>

        {/* Color Palette */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Color Palette</h2>
          
          {/* Nordic Blue */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-nordic-gray-800 mb-3">Nordic Blue (Primary)</h3>
            <div className="grid grid-cols-5 gap-2">
              <div className="bg-nordic-blue-100 h-20 rounded-lg flex items-center justify-center text-sm">100</div>
              <div className="bg-nordic-blue-300 h-20 rounded-lg flex items-center justify-center text-sm">300</div>
              <div className="bg-nordic-blue-500 h-20 rounded-lg flex items-center justify-center text-sm text-white">500</div>
              <div className="bg-nordic-blue-700 h-20 rounded-lg flex items-center justify-center text-sm text-white">700</div>
              <div className="bg-nordic-blue-900 h-20 rounded-lg flex items-center justify-center text-sm text-white">900</div>
            </div>
          </div>

          {/* Nordic Teal */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-nordic-gray-800 mb-3">Nordic Teal (Accent)</h3>
            <div className="grid grid-cols-5 gap-2">
              <div className="bg-nordic-teal-100 h-20 rounded-lg flex items-center justify-center text-sm">100</div>
              <div className="bg-nordic-teal-300 h-20 rounded-lg flex items-center justify-center text-sm">300</div>
              <div className="bg-nordic-teal-500 h-20 rounded-lg flex items-center justify-center text-sm text-white">500</div>
              <div className="bg-nordic-teal-700 h-20 rounded-lg flex items-center justify-center text-sm text-white">700</div>
              <div className="bg-nordic-teal-900 h-20 rounded-lg flex items-center justify-center text-sm text-white">900</div>
            </div>
          </div>

          {/* Nordic Gray */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-nordic-gray-800 mb-3">Nordic Gray (Neutral)</h3>
            <div className="grid grid-cols-5 gap-2">
              <div className="bg-nordic-gray-100 h-20 rounded-lg flex items-center justify-center text-sm border border-nordic-gray-300">100</div>
              <div className="bg-nordic-gray-300 h-20 rounded-lg flex items-center justify-center text-sm">300</div>
              <div className="bg-nordic-gray-500 h-20 rounded-lg flex items-center justify-center text-sm text-white">500</div>
              <div className="bg-nordic-gray-700 h-20 rounded-lg flex items-center justify-center text-sm text-white">700</div>
              <div className="bg-nordic-gray-900 h-20 rounded-lg flex items-center justify-center text-sm text-white">900</div>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Buttons</h2>
          <div className="flex flex-wrap gap-4">
            <button className="bg-nordic-blue-500 hover:bg-nordic-blue-600 active:bg-nordic-blue-700 
                             text-white font-medium px-4 py-2 rounded-md shadow-sm 
                             transition-colors duration-200">
              Primary Button
            </button>
            
            <button className="bg-white hover:bg-nordic-gray-50 active:bg-nordic-gray-100
                             text-nordic-gray-700 font-medium px-4 py-2 rounded-md 
                             border border-nordic-gray-300 shadow-sm
                             transition-colors duration-200">
              Secondary Button
            </button>
            
            <button className="bg-nordic-teal-500 hover:bg-nordic-teal-600 active:bg-nordic-teal-700
                             text-white font-medium px-4 py-2 rounded-md shadow-sm
                             transition-colors duration-200">
              Accent Button
            </button>

            <button className="bg-nordic-error hover:bg-red-600 active:bg-red-700
                             text-white font-medium px-4 py-2 rounded-md shadow-sm
                             transition-colors duration-200">
              Danger Button
            </button>
          </div>
        </section>

        {/* Cards */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-nordic-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-semibold text-nordic-gray-900 mb-2">Default Card</h3>
              <p className="text-nordic-gray-700">
                This is a default card with border and subtle shadow.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6">
              <h3 className="text-xl font-semibold text-nordic-gray-900 mb-2">Elevated Card</h3>
              <p className="text-nordic-gray-700">
                This card has more elevation and hover effect.
              </p>
            </div>

            <div className="bg-nordic-blue-50 border border-nordic-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-nordic-blue-900 mb-2">Colored Card</h3>
              <p className="text-nordic-blue-700">
                This card uses the Nordic blue color scheme.
              </p>
            </div>
          </div>
        </section>

        {/* Badges */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Badges</h2>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           bg-nordic-blue-100 text-nordic-blue-800">
              Primary
            </span>
            
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           bg-nordic-teal-100 text-nordic-teal-800">
              Active
            </span>
            
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           bg-green-100 text-green-800">
              Success
            </span>
            
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           bg-yellow-100 text-yellow-800">
              Warning
            </span>
            
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                           bg-red-100 text-red-800">
              Error
            </span>

            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium
                           bg-nordic-blue-500 text-white">
              3
            </span>
          </div>
        </section>

        {/* Form Inputs */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Form Inputs</h2>
          <div className="max-w-md space-y-4">
            <div>
              <label className="block text-sm font-medium text-nordic-gray-700 mb-1">
                Text Input
              </label>
              <input 
                type="text"
                className="w-full px-3 py-2 border border-nordic-gray-300 rounded-md
                         focus:outline-none focus:ring-2 focus:ring-nordic-blue-500 focus:border-transparent
                         placeholder:text-nordic-gray-400"
                placeholder="Enter text..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-nordic-gray-700 mb-1">
                Select
              </label>
              <select className="w-full px-3 py-2 border border-nordic-gray-300 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-nordic-blue-500 focus:border-transparent">
                <option>Option 1</option>
                <option>Option 2</option>
                <option>Option 3</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-nordic-gray-700 mb-1">
                Textarea
              </label>
              <textarea 
                className="w-full px-3 py-2 border border-nordic-gray-300 rounded-md
                         focus:outline-none focus:ring-2 focus:ring-nordic-blue-500 focus:border-transparent
                         placeholder:text-nordic-gray-400"
                rows={3}
                placeholder="Enter description..."
              />
            </div>
          </div>
        </section>

        {/* Alerts */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Alerts</h2>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md">
              <strong className="font-medium">Info:</strong> This is an informational message.
            </div>
            
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
              <strong className="font-medium">Success:</strong> Operation completed successfully!
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
              <strong className="font-medium">Warning:</strong> Please review before proceeding.
            </div>
            
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
              <strong className="font-medium">Error:</strong> Something went wrong.
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-nordic-gray-900 mb-6">Typography</h2>
          <div className="space-y-4">
            <h1 className="text-5xl font-bold text-nordic-gray-900">Heading 1</h1>
            <h2 className="text-4xl font-bold text-nordic-gray-900">Heading 2</h2>
            <h3 className="text-3xl font-semibold text-nordic-gray-900">Heading 3</h3>
            <h4 className="text-2xl font-semibold text-nordic-gray-900">Heading 4</h4>
            <h5 className="text-xl font-medium text-nordic-gray-900">Heading 5</h5>
            <h6 className="text-lg font-medium text-nordic-gray-900">Heading 6</h6>
            <p className="text-base text-nordic-gray-700">
              This is body text. Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
              Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>
            <p className="text-sm text-nordic-gray-600">
              This is small text. Ut enim ad minim veniam, quis nostrud exercitation ullamco.
            </p>
            <code className="font-mono text-sm bg-nordic-gray-100 px-2 py-1 rounded">
              const example = "code snippet";
            </code>
          </div>
        </section>
      </div>
    </div>
  );
};

